import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import {
  AdminCurveIntermediate,
  AdminInsertCurveIntermediateBody,
  AdminInsertSystemCurveIntermediateBody,
  AdminUpdateCurveIntermediateBody,
  AdminUpdateSystemCurveIntermediateBody,
} from "../../../../../http/patrol/types/curveIntermediates";
import { useCurvesIntermediatesApi } from "../../../../../http/patrol/useCurveIntermediates";
import { usePatrolSystemsApi } from "../../../../../http/patrol/usePatrolSystems";
import { usePatrolSteelVersionsApi } from "../../../../../http/patrol/useSteelVersion";
import { useSystemTypesApi } from "../../../../../http/patrol/useSystemTypes";
import { AdminItem, SearchAdminItem } from "../../../../../http/types/items";
import { useItemsApi } from "../../../../../http/useItems";
import { PaginationQuery } from "../../../../../types";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Inline from "../../../../ui/Inline";
import MultiCheckbox from "../../../../ui/MultiCheckbox";
import Numeric from "../../../../ui/Numeric";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import { CustomAsyncSelect } from "../../../../ui/select/CustomSelect";

export type CurveIntermediateFormBody = {
  item: AdminItem;
  extraItem?: AdminItem;
  minAngle?: number | null;
  maxAngle?: number | null;
  materialsIds: string[];
  systemTypesIds: string[];
  systemsIds: string[];
  visibility: VisibilityScopeValue;
};

type CurveIntermediateFormProps = {
  curveIntermediate?: AdminCurveIntermediate;
  onClose: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
};

const CurveIntermediateForm: FC<CurveIntermediateFormProps> = ({
  curveIntermediate,
  onClose,
  isDisabled,
  isLoading,
}) => {
  const i18n = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);
  const { patrolSystemId } = useParams<{ patrolSystemId: string }>();
  const aborter = useRef(new AbortController());
  const curveIntermediatesApi = useCurvesIntermediatesApi();
  const supportMaterialApi = usePatrolSteelVersionsApi();
  const systemTypesApi = useSystemTypesApi();
  const systemsApi = usePatrolSystemsApi();
  const itemsApi = useItemsApi();

  const pagination: PaginationQuery = {
    limit: 100,
  };

  const { data: systemTypes, error: systemTypesError } = systemTypesApi.useAdminSystemTypes(
    pagination,
    aborter.current.signal,
  );

  const { data: materials, error: materialsError } = supportMaterialApi.useAdminMaterials(
    pagination,
    aborter.current.signal,
  );

  const { data: systems, error: systemsError } = systemsApi.useAdminPatrolSystems(pagination, aborter.current.signal);

  // TODO
  // when insert or edit there could be an alert if the selected items are already present in the db.
  // if not, insert normally. if present, show warnings on common fields.
  // const { data, error, trigger } = curveIntermediatesApi.get....;

  useEffect(() => {
    (materialsError || systemTypesError || systemsError) &&
      dispatch(showErrorNotification(i18n, materialsError || systemTypesError || systemsError));
  }, [systemTypesError, materialsError, systemsError]);

  const defaultValues: Partial<CurveIntermediateFormBody> = {
    item: curveIntermediate?.item,
    extraItem: curveIntermediate?.extraItem,
    minAngle: curveIntermediate?.minAngle,
    maxAngle: curveIntermediate?.maxAngle,
    materialsIds: curveIntermediate?.materials.map((x) => x.id) ?? [],
    systemTypesIds: curveIntermediate?.systemTypes.map((x) => x.id) ?? [],
    systemsIds: curveIntermediate?.systems.map((x) => x.id) ?? [],
    visibility: curveIntermediate?.visibility,
  };

  return (
    <Form<CurveIntermediateFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting}
      onSubmit={() => async (formData) => {
        const sysUpdate: AdminUpdateSystemCurveIntermediateBody = {
          visibility: formData.visibility,
          minAngle: formData.minAngle ?? 0,
          maxAngle: formData.maxAngle ?? 0,
          materialsIds: formData.materialsIds,
          systemTypesIds: formData.systemTypesIds,
        };

        if (curveIntermediate?.item) {
          if (patrolSystemId) {
            try {
              await curveIntermediatesApi.updateAdminSystemCurveIntermediate(curveIntermediate.id, sysUpdate);
              onClose();
            } catch (error) {
              dispatch(showErrorNotification(i18n, error));
            } finally {
              setIsSubmitting(false);
            }
          } else {
            const update: AdminUpdateCurveIntermediateBody = {
              ...sysUpdate,
              systemsIds: formData.systemsIds,
            };

            try {
              await curveIntermediatesApi.updateAdminCurveIntermediate(curveIntermediate.id, update);
              onClose();
            } catch (error) {
              dispatch(showErrorNotification(i18n, error));
            } finally {
              setIsSubmitting(false);
            }
          }
        } else {
          if (patrolSystemId) {
            const insert: AdminInsertSystemCurveIntermediateBody = {
              itemId: formData.item.id,
              extraItemId: formData.extraItem?.id,
              ...sysUpdate,
              systemsIds: [patrolSystemId],
            };

            try {
              await curveIntermediatesApi.insertAdminSystemCurveIntermediate(insert);
              onClose();
            } catch (error) {
              dispatch(showErrorNotification(i18n, error));
            } finally {
              setIsSubmitting(false);
            }
          } else {
            const insert: AdminInsertCurveIntermediateBody = {
              itemId: formData.item.id,
              extraItemId: formData.extraItem?.id,
              ...sysUpdate,
              systemsIds: formData.systemsIds,
            };

            try {
              await curveIntermediatesApi.insertAdminCurveIntermediate(insert);
              onClose();
            } catch (error) {
              dispatch(showErrorNotification(i18n, error));
            } finally {
              setIsSubmitting(false);
            }
          }
        }
      }}
    >
      {({ control, setValue, watch, reset, formState: { errors } }) => {
        const materialsIds = watch("materialsIds");
        const systemTypesIds = watch("systemTypesIds");
        const systemsIds = watch("systemsIds");

        useEffect(() => {
          defaultValues?.item && reset(defaultValues);
        }, [defaultValues?.item]);

        const itemId = watch("item.id");
        const extraItemId = watch("extraItem.id");
        useEffect(() => {}, [itemId, extraItemId]);

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                      {({ labelId }) => {
                        const searchTerm = useRef("");
                        const aborter = useRef(new AbortController());

                        const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                        // Fetch initial data on mount
                        useEffect(() => {
                          loadOptions(searchTerm.current).then(setDefaultOptions);
                        }, []);

                        return (
                          <CustomAsyncSelect
                            {...field}
                            inputId={labelId}
                            defaultOptions={defaultOptions}
                            loadOptions={loadOptions}
                            isDisabled={isDisabled || !!curveIntermediate?.id}
                            isClearable
                            getOptionLabel={(x) => x.code}
                            getOptionValue={(x) => x.id}
                            // onChange={(value) => setValue("item", value ?? null)}
                          />
                        );
                      }}
                    </FormField>
                  </>
                );
              }}
            />

            <Controller
              name="extraItem"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <FormField label={i18n.translation.items.singular} error={fieldState.error} isOptional>
                      {({ labelId }) => {
                        const searchTerm = useRef("");
                        const aborter = useRef(new AbortController());

                        const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                        // Fetch initial data on mount
                        useEffect(() => {
                          loadOptions(searchTerm.current).then(setDefaultOptions);
                        }, []);

                        return (
                          <CustomAsyncSelect
                            {...field}
                            inputId={labelId}
                            defaultOptions={defaultOptions}
                            loadOptions={loadOptions}
                            isDisabled={isDisabled || !!curveIntermediate?.id}
                            isClearable
                            getOptionLabel={(x) => x.code}
                            getOptionValue={(x) => x.id}
                            // onChange={(value) => setValue("extraItem", value ?? null)}
                          />
                        );
                      }}
                    </FormField>
                  </>
                );
              }}
            />
            <FormField
              label={`${i18n.translation.common.angle} (${i18n.translation.common.min}/${i18n.translation.common.max})`}
              error={
                (errors.maxAngle ?? errors.maxAngle)?.type === "validate" ||
                (errors.maxAngle ?? errors.maxAngle)?.type === "max"
                  ? (errors.maxAngle ?? errors.maxAngle)
                  : undefined
              }
              warning={
                !!patrolSystemId &&
                curveIntermediate &&
                (defaultValues.minAngle != watch("minAngle") || defaultValues.maxAngle != watch("maxAngle"))
                  ? i18n.translation.common.warningThisEditCouldAffectOtherSystemsAsWell
                  : undefined
              }
            >
              <Inline>
                <Controller
                  name="minAngle"
                  control={control}
                  rules={{
                    required: true,
                    min: 1,
                    validate: (value) => {
                      if (value) {
                        const maxAngle = watch("maxAngle");
                        if (maxAngle && maxAngle < value) {
                          return "maxAngle";
                        }
                      }
                      return undefined;
                    },
                  }}
                  disabled={isSubmitting}
                  render={({ field, fieldState }) => (
                    <FormField
                      error={
                        fieldState.error?.type !== "validate" && fieldState.error?.type !== "max"
                          ? fieldState.error
                          : undefined
                      }
                      fullWidth
                    >
                      {({ labelId, isInvalid }) => (
                        <Numeric
                          {...field}
                          id={labelId}
                          isInvalid={isInvalid}
                          decimalScale={0}
                          suffix=" °"
                          allowNegative={false}
                          onBlur={(value) => setValue("minAngle", value)}
                        />
                      )}
                    </FormField>
                  )}
                />

                <Controller
                  name="maxAngle"
                  control={control}
                  rules={{
                    required: true,
                    min: 1,
                    max: 180,
                    validate: (value) => {
                      if (value) {
                        const minAngle = watch("minAngle");
                        if (minAngle && minAngle > value) {
                          return "minAngle";
                        }
                      }
                      return undefined;
                    },
                  }}
                  disabled={isSubmitting}
                  render={({ field, fieldState }) => (
                    <FormField
                      error={
                        fieldState.error?.type !== "validate" && fieldState.error?.type !== "max"
                          ? fieldState.error
                          : undefined
                      }
                      fullWidth
                    >
                      {({ labelId, isInvalid }) => (
                        <Numeric
                          {...field}
                          id={labelId}
                          isInvalid={isInvalid}
                          decimalScale={0}
                          suffix=" °"
                          allowNegative={false}
                          onBlur={(value) => setValue("maxAngle", value)}
                        />
                      )}
                    </FormField>
                  )}
                />
              </Inline>
            </FormField>

            <FormField
              label={i18n.translation.materials.plural}
              warning={
                patrolSystemId && curveIntermediate && !sameIds(defaultValues.materialsIds ?? [], watch("materialsIds"))
                  ? i18n.translation.common.warningThisEditCouldAffectOtherSystemsAsWell
                  : undefined
              }
            >
              <MultiCheckbox
                values={
                  materials?.items.map((x) => ({
                    id: x.id,
                    title: x.name,
                    selected: materialsIds?.includes(x.id),
                  })) ?? []
                }
                disabled={isDisabled || isLoading}
                onUpdate={(id, checked) => {
                  setValue("materialsIds", checked ? [...materialsIds, id] : materialsIds.filter((x) => x !== id));
                }}
              />
            </FormField>

            <FormField
              label={i18n.translation.systemTypes.plural}
              warning={
                patrolSystemId &&
                curveIntermediate &&
                !sameIds(defaultValues.systemTypesIds ?? [], watch("systemTypesIds"))
                  ? i18n.translation.common.warningThisEditCouldAffectOtherSystemsAsWell
                  : undefined
              }
            >
              <MultiCheckbox
                values={
                  systemTypes?.items.map((x) => ({
                    id: x.id,
                    title: x.name,
                    selected: systemTypesIds?.includes(x.id),
                  })) ?? []
                }
                disabled={isDisabled || isLoading}
                onUpdate={(id, checked) => {
                  setValue(
                    "systemTypesIds",
                    checked ? [...systemTypesIds, id] : systemTypesIds.filter((x) => x !== id),
                  );
                }}
              />
            </FormField>

            <FormField label={i18n.translation.patrolSystems.plural} hidden={!!patrolSystemId}>
              <MultiCheckbox
                values={
                  systems?.items
                    .filter((x) => x.isLifeLine)
                    .map((x) => ({
                      id: x.id,
                      title: x.name,
                      selected: systemsIds?.includes(x.id),
                    })) ?? []
                }
                disabled={isDisabled || isLoading}
                onUpdate={(id, checked) => {
                  setValue("systemsIds", checked ? [...systemsIds, id] : systemsIds.filter((x) => x !== id));
                }}
              />
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.common.visibility}
                  warning={
                    patrolSystemId && curveIntermediate && defaultValues?.visibility !== watch("visibility")
                      ? i18n.translation.common.warningThisEditCouldAffectOtherSystemsAsWell
                      : undefined
                  }
                  error={fieldState.error}
                  isOptional
                >
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default CurveIntermediateForm;

const sameIds = (ids1: string[], ids2: string[]) => {
  if (ids1.length !== ids2.length) {
    return false;
  }

  const norm1 = ids1
    .map((s) => s.toLowerCase())
    .sort()
    .join("");
  const norm2 = ids2
    .map((s) => s.toLowerCase())
    .sort()
    .join("");

  return norm1 === norm2;
};
